import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import CodeMirror from './common/CodeMirrorWithWrap'
import { sql } from '@codemirror/lang-sql'
import { yaml } from '@codemirror/lang-yaml'
import { oneDark } from '@codemirror/theme-one-dark'
import { search, searchKeymap, selectSelectionMatches } from '@codemirror/search'
import { keymap } from '@codemirror/view'
import { Prec } from '@codemirror/state'
import { api } from '../api'
import { APP_DEFAULTS } from '../defaults'
import SearchInput from './common/SearchInput'
import HighlightText from './common/HighlightText'
import { filterByText } from '../utils/filterByText'
import { downloadText, ensureExtension } from '../utils/download'
import { suggestDuplicateName } from '../utils/duplicateName'
import DuplicateDialog from './common/DuplicateDialog'
import SvRecommendDialog from './common/SvRecommendDialog'
import TokenBadge from './common/TokenBadge'
import { extractViewSummariesFromYaml } from '../utils/yamlExtract'
import { copyToClipboard } from '../utils/clipboard'

const SPINNER_MESSAGES = [
  'SQL 쿼리 구조를 분석하고 있습니다...',
  'FROM/JOIN 절에서 테이블을 추출하고 있습니다...',
  'WHERE 조건과 필터를 파싱하고 있습니다...',
  'Oracle 문법을 감지하고 있습니다 (NVL, DECODE, TO_CHAR...)',
  'CTE(WITH 절) 구조를 분석하고 있습니다...',
  'SUM/COUNT 등 집계 함수를 분류하고 있습니다...',
  'Dataplex Glossary 용어를 조회하고 있습니다...',
  'Gemini에게 Semantic View 변환을 요청하고 있습니다...',
  'Gemini가 SQL을 Semantic View YAML로 변환하고 있습니다...',
  'CASE WHEN 표현식을 dimension으로 매핑하고 있습니다...',
  '집계 컬럼을 measure로 분류하고 있습니다...',
  'Oracle → BigQuery 문법을 변환하고 있습니다...',
  'dimension_group 계층 구조를 설계하고 있습니다...',
  'source/join/filter 구조를 생성하고 있습니다...',
  '생성된 YAML을 검증하고 있습니다...',
  'YAML 구조 오류를 자동 수정하고 있습니다...',
  'Gemini가 열심히 변환하고 있습니다...',
  '거의 완료되었습니다...',
]

/**
 * SQL → Semantic View Converter (별도 페이지)
 *
 * 변환 결과가 단일 semantic_view인지 복수 semantic_views(composite)인지
 * 자동 감지하여 적절한 저장소에 저장한다.
 *
 * 레이아웃:
 *   ┌──────────────┬─────────────────────┬─────────────────────┐
 *   │ Saved SQLs    │  SQL Editor          │  YAML Editor         │
 *   │ ──────────── │  (CodeMirror SQL)    │  (CodeMirror YAML)   │
 *   │ Reference     │                     │  [결과 유형 표시]      │
 *   │ Views (체크)   │                     │                      │
 *   └──────────────┴─────────────────────┴─────────────────────┘
 */

/* ── YAML 결과 유형 감지 ─────────────────────────────────────────────────── */
function detectYamlType(yamlStr) {
  if (!yamlStr || !yamlStr.trim()) return null
  const trimmed = yamlStr.trim()
  // KBNL-115: '---' 는 '문서 시작 표시'로도 쓰여, 선행 단독 '---' 만으로 composite 로
  //   오분류되던 문제(리뷰). '---' 유무가 아니라 '실제 문서 조각 수(2개 이상)'로 판정.
  const docs = trimmed.split(/^---\s*$/m).map(s => s.trim()).filter(Boolean)
  const isSvDoc = (d) => /^semantic_view\s*:/m.test(d) || /^-\s*semantic_view\s*:/m.test(d)
  if (docs.length >= 2 && docs.every(isSvDoc)) return 'composite'
  // semantic_views: (하위 호환) → composite
  if (/^semantic_views\s*:/m.test(trimmed)) return 'composite'
  // - semantic_view: (하위 호환 리스트)
  if (/^-\s*semantic_view\s*:/m.test(trimmed)) return 'composite'
  // 단일 semantic_view: → single
  if (/^semantic_view\s*:/m.test(trimmed)) return 'single'
  return 'unknown'
}

function typeLabel(type) {
  if (type === 'single') return 'Semantic View (단일)'
  if (type === 'composite') return 'Composite View (복합)'
  if (type === 'unknown') return '알 수 없는 형식'
  return ''
}

function typeBadgeClass(type) {
  if (type === 'single') return 'type-single'
  if (type === 'composite') return 'type-composite'
  return 'type-unknown'
}

/* ── YAML에서 semantic_view 이름을 추출 ──────────────────────────────── */
function extractViewNamesFromYaml(yamlStr) {
  const names = []
  const blocks = yamlStr.split(/semantic_view\s*:/)
  for (const block of blocks.slice(1)) {
    const m = block.match(/^\s*name:\s*(.+)$/m)
    if (m) names.push(m[1].trim().replace(/^["']|["']$/g, ''))
  }
  return names
}

// extractViewSummariesFromYaml 은 utils/yamlExtract.js 로 이동 (3개 컴포넌트 공유).

export default function SqlConverter({ showToast, storageSettings, editorTheme, settings, onNavigateToView }) {
  // ── 저장된 SQL 목록 ─────────────────────────────────────────────────
  const [savedQueries, setSavedQueries] = useState([])
  const [selectedQuery, setSelectedQuery] = useState(null)
  // Saved SQL 검색 필터 — name + description 으로 필터
  const [sqlQuery, setSqlQuery] = useState('')
  const [queryName, setQueryName] = useState('')
  const [queryDesc, setQueryDesc] = useState('')
  // Semantic/Composite View 저장 시 사용할 별도 이름.
  // 비어 있으면 queryName 과 같은 이름으로 저장 (기본 동작).
  const [viewName, setViewName] = useState('')
  // 로드 시점의 View 이름(과 그 yamlType) — 의도된 갱신과 우연한 덮어쓰기를 구분하기 위함.
  // null 이면 새로 만드는 중 → 모든 View 이름 충돌이 덮어쓰기 위험.
  const [loadedView, setLoadedView] = useState(null) // { name, type } | null

  // ── 에디터 내용 ─────────────────────────────────────────────────────
  const [sqlText, setSqlText] = useState('')
  const [semanticYaml, setSemanticYaml] = useState('')

  // ── 참조뷰 (Semantic Views + Composite Views) ────────────────────────
  // 저장 시 동명 Semantic/Composite View 중복 체크용 (선택 후 confirm) — UI 표시는 없음.
  const [savedMetricViews, setSavedMetricViews] = useState([])
  const [savedCompositeViews, setSavedCompositeViews] = useState([])

  // ── 이름 바꾸기 ────────────────────────────────────────────────────
  const [renamingQuery, setRenamingQuery] = useState(null)
  const [renameValue, setRenameValue] = useState('')

  // ── 복제 (Duplicate) ───────────────────────────────────────────────
  // 사이드바 항목별 hover 액션. 다이얼로그에서 새 이름을 확인 받은 뒤
  // 원본 SQL 본문 + 연결된 SV/CV 까지 같은 이름 규칙으로 함께 복제한다.
  const [duplicateDialog, setDuplicateDialog] = useState(null) // { sourceName, defaultName } | null
  const [duplicating, setDuplicating] = useState(false)

  // ── 비동기 상태 ─────────────────────────────────────────────────────
  const [converting, setConverting] = useState(false)
  const [saving, setSaving] = useState(false)
  // 저장 시 동명 레코드 덮어쓰기 확인 다이얼로그 상태.
  // null 이면 닫힘. 열릴 때는 경고할 항목 목록을 함께 들고 있는다.
  const [overwriteDialog, setOverwriteDialog] = useState(null) // { overwrites: string[] } | null
  const [elapsed, setElapsed] = useState(0)
  const timerRef = useRef(null)

  // ── 스피너 메시지 ─────────────────────────────────────────────────
  const [spinnerIdx, setSpinnerIdx] = useState(0)
  const spinnerIntervalRef = useRef(null)

  useEffect(() => {
    if (converting) {
      setSpinnerIdx(0)
      spinnerIntervalRef.current = setInterval(() => {
        setSpinnerIdx(prev => (prev + 1) % SPINNER_MESSAGES.length)
      }, 3000)
    } else {
      if (spinnerIntervalRef.current) {
        clearInterval(spinnerIntervalRef.current)
        spinnerIntervalRef.current = null
      }
    }
    return () => { if (spinnerIntervalRef.current) clearInterval(spinnerIntervalRef.current) }
  }, [converting])

  // ── 스플리터 ────────────────────────────────────────────────────────
  const [editorPct, setEditorPct] = useState(50)
  const containerRef = useRef(null)

  // ── 추가 요구사항 ───────────────────────────────────────────────────
  const [requirements, setRequirements] = useState('')

  // ── YAML 결과 유형 자동 감지 ────────────────────────────────────────
  const yamlType = useMemo(() => detectYamlType(semanticYaml), [semanticYaml])

  // ── 저장된 SQL 목록 로드 ────────────────────────────────────────────
  const loadQueries = useCallback(async () => {
    try {
      const list = await api.listSqlQueries(storageSettings)
      setSavedQueries(list)
    } catch (e) {
      console.error('Failed to load SQL queries:', e)
      showToast('error', 'SQL 쿼리 목록 로드 실패: ' + e.message)
    }
  }, [storageSettings])

  useEffect(() => { loadQueries() }, [loadQueries])

  // 저장된 Metric / Composite View 목록 로드 — 저장 시 동명 중복 체크에 사용.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const [metrics, composites] = await Promise.all([
          api.listViews(storageSettings).catch(() => []),
          api.listCompositeViews(storageSettings).catch(() => []),
        ])
        if (!cancelled) {
          setSavedMetricViews(metrics)
          setSavedCompositeViews(composites)
        }
      } catch (e) {
        console.error('Failed to load views:', e)
        showToast('error', '뷰 목록 로드 실패: ' + e.message)
      }
    })()
    return () => { cancelled = true }
  }, [storageSettings])

  // ── SQL 선택(로드) ──────────────────────────────────────────────────
  // KBNL-49 협업 모드: 다른 사용자의 SQL 도 로드 가능. selectedQueryIsMine 으로
  // 본인 것 여부를 추적해 rename/delete 차단 + 저장은 본인 scope 로 자연 복사됨.
  const [selectedQueryIsMine, setSelectedQueryIsMine] = useState(true)
  // KBNL-141(리뷰): 협업 모드 동명이인 충돌 방지 — 선택/리네임을 owner 까지 구분.
  const [selectedQueryOwner, setSelectedQueryOwner] = useState('')
  // KBNL-69: SQL 본문 헤더에 표시할 토큰 수 (마지막 저장 기준 — 백엔드는 sql_text 만 카운트)
  const [selectedQueryTokenCount, setSelectedQueryTokenCount] = useState(null)
  // KBNL-72: YAML 헤더에 표시할 토큰 수.
  //   - 변환 직후: convert 응답의 token_count
  //   - 저장 직후: SV/CV 저장 응답의 token_count
  //   - YAML 직접 편집 시: 사용자가 손대면 stale 이라 null 로 리셋 (다음 변환/저장 시 다시 채워짐)
  const [yamlTokenCount, setYamlTokenCount] = useState(null)

  // ── KBNL-74: 유사 SV 찾기 (하이브리드 서치) ──────────────────────────
  // 변환 옆 "유사 SV 찾기" 버튼 → 모달(SvRecommendDialog) 오픈 → top-N 후보.
  // 행 클릭 시 onNavigateToView(name) 콜백 — 해당 SV 화면으로 이동 (single navigation).
  //
  // 결과 유형(yamlType)에 따라 검색 기준이 달라진다 (KBNL-74 후속):
  //   - 변환 전              : sqlText 기반 (mode='sql')
  //   - single SV            : 변환된 SV 의 name+description+tables 기반 (mode='query')
  //   - composite (CV)       : sub-SV 목록 picker → 선택한 sub-SV 기준 (mode='query')
  // 랭킹 품질: 변환 결과 SV 는 query 텍스트가 아니라 seed_sv + override_yaml 로
  // 검색 (vector 질의 풍부 + identifier 채널 활성 + 동명 저장본 exclude).
  // recommendSeed 비어있으면 sqlText fallback (mode='sql'). { name, description, yaml } | null
  const [recommendOpen, setRecommendOpen] = useState(false)
  const [recommendSeed, setRecommendSeed] = useState(null)
  const [recommendPickerOpen, setRecommendPickerOpen] = useState(false)
  const recommendPickerRef = useRef(null)

  const handleSelectQuery = useCallback(async (name, ownerUserId = '') => {
    try {
      // 같은 이름이 여러 owner 에 있을 때 정확한 항목 로드 — list item 에서 owner_user_id 전달.
      const data = await api.getSqlQuery(name, storageSettings, ownerUserId)
      setSelectedQuery(name)
      setSelectedQueryIsMine(data.is_mine !== false)
      setSelectedQueryOwner(data.owner_user_id || data.user_id || ownerUserId || '')
      setSelectedQueryTokenCount(data.token_count ?? null)
      // KBNL-72: SQL 로드 응답에는 연결된 SV/CV 의 token_count 가 없음 — null 로 두면 배지가 '—'.
      // 다음 변환/저장 시 갱신됨. (향후: get_sql_query 응답에 linked SV token_count 포함 검토)
      setYamlTokenCount(null)
      setQueryName(data.name || name)
      setQueryDesc(data.description || '')
      setSqlText(data.sql_text || '')
      setSemanticYaml(data.semantic_yaml || '')
      setRequirements(data.conversion_settings?.user_requirements || '')
      // 저장된 별도 View 이름이 있으면 복원, 없으면 비워둠 (queryName 과 동일 사용)
      const loadedViewNameRaw = data.conversion_settings?.view_name || ''
      setViewName(loadedViewNameRaw)
      // 로드 시점의 View 이름과 유형을 기록 — 같은 이름/유형으로 다시 저장하면 의도된 갱신.
      const loadedYamlType = detectYamlType(data.semantic_yaml || '')
      setLoadedView(loadedYamlType
        ? { name: loadedViewNameRaw.trim() || (data.name || name), type: loadedYamlType }
        : null)
      const ownerHint = data.is_mine === false
        ? ` (작성자: ${data.owner_name || data.owner_user_id || '?'} — 저장 시 내 워크스페이스에 복사)`
        : ''
      showToast('success', `'${name}' 로드 완료${ownerHint}`)
    } catch (e) {
      showToast('error', `로드 실패: ${e.message}`)
    }
  }, [storageSettings, showToast])

  // ── SQL 저장 ────────────────────────────────────────────────────────
  // SQL 쿼리는 항상 SQL 스토리지에 저장.
  // YAML 은 결과 유형에 따라 단일 스토리지에만 저장한다 (single → Semantic View, composite → Composite View).
  // 어디에 저장됐는지 / 어디가 실패했는지를 토스트로 정확히 노출한다.
  // 실제 저장 로직 — handleSave 의 검사 단계 또는 다이얼로그 확인 후 호출.
  const proceedSave = useCallback(async () => {
    const name = queryName.trim()
    const vName = viewName.trim() || name
    const namesDiffer = vName !== name

    setSaving(true)

    const savedTo = []                 // ['SQL Query', 'Semantic View(name)', ...]
    const failedAt = []                // [{ where, message }]

    try {
      // 1) SQL 쿼리 스토리지에 항상 저장 (실패 시 전체 catch 로 빠져나감)
      const sqlSaveRes = await api.saveSqlQuery({
        name,
        description: queryDesc,
        sql_text: sqlText,
        semantic_yaml: semanticYaml,
        conversion_settings: {
          user_requirements: requirements,
          model_name: settings?.model_name || APP_DEFAULTS.model_name,
          glossary_id: settings?.glossary_id || '',
          glossary_ids: Array.isArray(settings?.glossary_ids) ? settings.glossary_ids : [],
          result_type: yamlType,
          // 다음 로드 시 별도 View 이름 복원용
          view_name: viewName.trim(),
        },
      }, storageSettings)
      savedTo.push(namesDiffer ? `SQL Query '${name}'` : 'SQL Query')
      // KBNL-69: 저장 응답의 token_count 로 본문 헤더 배지 갱신
      setSelectedQueryTokenCount(sqlSaveRes?.token_count ?? null)

      // 2) Semantic YAML 이 있으면 유형에 따라 단일 대상 스토리지에 저장
      if (semanticYaml.trim()) {
        const desc = queryDesc + (queryDesc ? ' ' : '') + '(SQL Converter)'
        const viewNames = extractViewNamesFromYaml(semanticYaml)
        const sources = viewNames.map(vn => ({
          view_name: vn,
          origin: 'sql_converter',
          sql_query_name: name,
          sql_text: sqlText,
        }))

        if (yamlType === 'single') {
          // 단일 semantic_view → Semantic View(Metric View) 스토리지에만 저장
          try {
            const svRes = await api.saveView({
              name: vName,
              description: desc,
              semantic_yaml: semanticYaml,
              sql_text: sqlText,
            }, storageSettings)
            savedTo.push(namesDiffer ? `Semantic View '${vName}'` : 'Semantic View')
            // KBNL-72: 저장된 SV 의 token_count 로 YAML 헤더 배지 갱신
            if (svRes?.token_count != null) setYamlTokenCount(svRes.token_count)
          } catch (e) {
            failedAt.push({ where: `Semantic View '${vName}'`, message: e.message })
          }
        } else if (yamlType === 'composite') {
          // 복합 semantic_views → Composite View 스토리지에만 저장
          try {
            const cvRes = await api.saveCompositeView({
              name: vName,
              description: desc,
              // KBNL-75: yaml_string → composite_yaml (alias 로 legacy 키도 수용)
              composite_yaml: semanticYaml,
              selected_views: [],
              sql_text: sqlText,
              sources,
            }, storageSettings)
            savedTo.push(namesDiffer ? `Composite View '${vName}'` : 'Composite View')
            // KBNL-72: 저장된 CV 의 token_count 로 YAML 헤더 배지 갱신
            if (cvRes?.token_count != null) setYamlTokenCount(cvRes.token_count)
          } catch (e) {
            failedAt.push({ where: `Composite View '${vName}'`, message: e.message })
          }
        } else {
          // unknown 또는 null — YAML 저장은 스킵하고 사용자에게 알림
          showToast('warning', 'YAML 형식을 인식하지 못해 Semantic/Composite View 스토리지에는 저장하지 않았습니다. (SQL 쿼리만 저장)')
        }
      }

      setSelectedQuery(name)
      // KBNL-141(리뷰): 저장 후 선택 owner 도 본인으로 — 목록 선택 키(owner::name) 정확화.
      setSelectedQueryIsMine(true)
      setSelectedQueryOwner(sqlSaveRes?.owner_user_id || storageSettings?.user_id || '')
      await loadQueries()

      // 결과 토스트 — 성공/실패 스토리지를 모두 노출
      if (failedAt.length > 0) {
        const okList = savedTo.join(', ')
        const failList = failedAt.map(f => `${f.where}: ${f.message}`).join(' / ')
        showToast('warning', `일부 저장 실패 — 성공 [${okList}] · 실패 [${failList}]`)
      } else {
        const list = savedTo.join(' + ')
        showToast('success', `저장 완료 (${list})`)
      }
    } catch (e) {
      showToast('error', `저장 실패: ${e.message}`)
    } finally {
      setSaving(false)
    }
  }, [queryName, queryDesc, viewName, sqlText, semanticYaml, requirements, settings, storageSettings, showToast, loadQueries, yamlType, savedCompositeViews, savedMetricViews])

  // 저장 진입점 — 입력 검증 + 동명 레코드 충돌 검사.
  // 충돌이 있으면 다이얼로그를 띄우고 사용자가 확인하면 proceedSave 가 호출된다.
  const handleSave = useCallback(() => {
    const name = queryName.trim()
    if (!name) { showToast('error', '이름을 입력하세요.'); return }
    if (!sqlText.trim()) { showToast('error', 'SQL을 입력하세요.'); return }
    const vName = viewName.trim() || name

    // 의도된 갱신(현재 편집 중인 SQL/View) 은 경고하지 않고,
    // 다른 레코드를 덮어쓸 위험이 있을 때만 다이얼로그.
    const overwrites = []  // ["SQL Query 'A'", "Semantic View 'B'", ...]

    // 1) SQL Query — 저장은 항상 본인 scope 라 본인 것끼리만 충돌 검사 (KBNL-49).
    //    selectedQueryIsMine && name===selectedQuery 면 의도된 update → confirm 불필요.
    const myQueries = savedQueries.filter(q => q.is_mine !== false)
    const isMyOwnUpdate = selectedQueryIsMine && name === selectedQuery
    if (myQueries.some(q => q.name === name) && !isMyOwnUpdate) {
      overwrites.push(`SQL Query '${name}'`)
    }

    // 2) Semantic/Composite View — loadedView 와 같은 이름/유형이면 갱신, 그 외엔 충돌.
    //    뷰 목록도 협업 모드라 본인 것끼리만 충돌 검사.
    if (semanticYaml.trim() && (yamlType === 'single' || yamlType === 'composite')) {
      const targetListAll = yamlType === 'single' ? savedMetricViews : savedCompositeViews
      const targetList = targetListAll.filter(v => v.is_mine !== false)
      const targetLabel = yamlType === 'single' ? 'Semantic View' : 'Composite View'
      const isIntentionalUpdate = loadedView
        && loadedView.type === yamlType
        && loadedView.name === vName
      if (targetList.some(v => v.name === vName) && !isIntentionalUpdate) {
        overwrites.push(`${targetLabel} '${vName}'`)
      }
    }

    if (overwrites.length > 0) {
      setOverwriteDialog({ overwrites })
      return
    }
    proceedSave()
  }, [queryName, sqlText, viewName, semanticYaml, yamlType, savedQueries, selectedQuery, selectedQueryIsMine, savedMetricViews, savedCompositeViews, loadedView, showToast, proceedSave])

  // 다이얼로그에서 "덮어쓰기" 확인 시 호출
  const confirmOverwrite = useCallback(() => {
    setOverwriteDialog(null)
    proceedSave()
  }, [proceedSave])

  // ── SQL 삭제 ────────────────────────────────────────────────────────
  const handleDelete = useCallback(async () => {
    if (!selectedQuery) return
    // KBNL-49: 다른 사용자의 SQL 은 삭제 불가 (backend 도 본인 scope 만 보지만 UI 단계에서 차단).
    if (!selectedQueryIsMine) {
      showToast('error', '다른 사용자의 SQL 은 삭제할 수 없습니다.')
      return
    }
    if (!window.confirm(`'${selectedQuery}'을(를) 삭제할까요?`)) return
    try {
      await api.deleteSqlQuery(selectedQuery, storageSettings)
      setSelectedQuery(null)
      setSelectedQueryIsMine(true)
      setSelectedQueryOwner('')
      setSelectedQueryTokenCount(null)
      setYamlTokenCount(null)
      setQueryName('')
      setQueryDesc('')
      setViewName('')
      setLoadedView(null)
      setSqlText('')
      setSemanticYaml('')
      await loadQueries()
      showToast('success', '삭제 완료')
    } catch (e) {
      showToast('error', `삭제 실패: ${e.message}`)
    }
  }, [selectedQuery, selectedQueryIsMine, storageSettings, showToast, loadQueries])

  // ── 새로 만들기 ─────────────────────────────────────────────────────
  const handleNew = useCallback(() => {
    setSelectedQuery(null)
    setSelectedQueryIsMine(true)
    setSelectedQueryOwner('')
    setSelectedQueryTokenCount(null)
    setQueryName('')
    setQueryDesc('')
    setViewName('')
    setLoadedView(null)
    setSqlText('')
    setSemanticYaml('')
    setRequirements('')
  }, [])

  // ── SQL 이름 바꾸기 (더블클릭 → inline input) ─────────────────────
  // SQL Query 스토리지의 이름만 변경한다. 같은 이름으로 저장되어 있던
  // Semantic/Composite View 는 별도 페이지에서 따로 rename 한다.
  // KBNL-141(리뷰): renamingQuery 는 owner::name 키로 보관 — 같은 이름 다른 owner 행이
  // 동시에 rename 입력으로 바뀌던 문제 방지. rename 은 본인 항목에서만 시작됨.
  const handleStartRename = useCallback((q) => {
    setRenamingQuery(`${q.owner_user_id || ''}::${q.name}`)
    setRenameValue(q.name)
  }, [])

  const handleCancelRename = useCallback(() => {
    setRenamingQuery(null)
  }, [])

  const handleConfirmRename = useCallback(async () => {
    const renKey = renamingQuery
    if (!renKey) return
    const sep = renKey.indexOf('::')
    const oldOwner = sep >= 0 ? renKey.slice(0, sep) : ''
    const oldName = sep >= 0 ? renKey.slice(sep + 2) : renKey
    const newName = renameValue.trim()
    if (!oldName) return
    if (!newName || newName === oldName) {
      setRenamingQuery(null)
      return
    }
    // 본인 것끼리만 충돌 검사 (backend 가 본인 scope 에서 rename 하므로).
    const myQueries = savedQueries.filter(q => q.is_mine !== false)
    if (myQueries.some(q => q.name === newName)) {
      showToast('error', `이미 '${newName}' 이름의 SQL 이 존재합니다.`)
      setRenamingQuery(null)
      return
    }
    try {
      await api.renameSqlQuery(oldName, newName, storageSettings)
      // 현재 편집 중인 SQL 이 이름이 바뀐 SQL 이면 동기화 (owner 까지 일치할 때만)
      if (selectedQuery === oldName && selectedQueryOwner === oldOwner) {
        setSelectedQuery(newName)
        setQueryName(newName)
      }
      await loadQueries()
      showToast('success', `'${oldName}' → '${newName}' 이름 변경 완료`)
    } catch (e) {
      showToast('error', `이름 변경 실패: ${e.message}`)
    } finally {
      setRenamingQuery(null)
    }
  }, [renamingQuery, renameValue, selectedQuery, selectedQueryOwner, savedQueries, storageSettings, showToast, loadQueries])

  // ── SQL 복제 (Duplicate) ──────────────────────────────────────────
  // 본인 항목만 복제 가능. 사이드바 hover 시 노출되는 ⎘ 버튼 → 다이얼로그 진입.
  // 다이얼로그가 확정되면 SQL doc 자체 + 연결된 Semantic/Composite View 까지
  // 같은 접미사로 한 번에 복제한다 (UX 일관성).
  const openDuplicate = useCallback((q) => {
    const myQueries = (savedQueries || []).filter(x => x.is_mine !== false)
    setDuplicateDialog({
      sourceName: q.name,
      defaultName: suggestDuplicateName(q.name, myQueries),
    })
  }, [savedQueries])

  const handleDuplicate = useCallback(async (newName) => {
    if (!duplicateDialog) return
    const sourceName = duplicateDialog.sourceName
    if (!newName || !newName.trim()) { showToast('error', '이름을 입력하세요.'); return }
    const finalName = newName.trim()
    const myQueries = (savedQueries || []).filter(x => x.is_mine !== false)
    if (myQueries.some(q => q.name === finalName)) {
      showToast('error', `이미 '${finalName}' 이름의 SQL 이 존재합니다.`)
      return
    }
    setDuplicating(true)
    try {
      // 1) 원본 SQL doc 로드 (현재 selectedQuery 와 다를 수 있으므로 재조회)
      const src = await api.getSqlQuery(sourceName, storageSettings)

      // 2) 연결된 view 이름 결정.
      //   원본의 view_name 이 sql 이름과 같으면 → 새 SQL 이름과 동일하게 (newName).
      //   원본이 별도 view_name 을 갖고 있으면 → 그 이름에 _copy 접미사.
      const origViewName = (src.conversion_settings?.view_name || '').trim() || sourceName
      const viewNameMatchesSql = origViewName === sourceName
      const myMetricViews     = (savedMetricViews || []).filter(v => v.is_mine !== false)
      const myCompositeViews  = (savedCompositeViews || []).filter(v => v.is_mine !== false)
      const dupViewName = viewNameMatchesSql
        ? finalName
        : suggestDuplicateName(origViewName, [...myMetricViews, ...myCompositeViews])

      // 3) SQL doc 저장 — 본문 그대로, 이름만 교체. conversion_settings.view_name 은
      //    위에서 정한 dupViewName 으로 갱신해 다음 로드 시 연결이 유지되도록 한다.
      await api.saveSqlQuery({
        ...src,
        name: finalName,
        conversion_settings: {
          ...(src.conversion_settings || {}),
          view_name: viewNameMatchesSql ? '' : dupViewName,
        },
      }, storageSettings)

      // 4) 연결된 SV/CV 가 있으면 함께 복제 (본인 워크스페이스에 한해서).
      const linkedSV = myMetricViews.find(v => v.name === origViewName)
      const linkedCV = myCompositeViews.find(v => v.name === origViewName)
      const copiedExtras = []
      if (linkedSV) {
        try {
          const sv = await api.getView(origViewName, storageSettings)
          await api.saveView({ ...sv, name: dupViewName }, storageSettings)
          copiedExtras.push(`Semantic View '${dupViewName}'`)
        } catch (e) {
          showToast('warning', `Semantic View 복제 실패: ${e.message}`)
        }
      }
      if (linkedCV) {
        try {
          const cv = await api.getCompositeView(origViewName, storageSettings)
          await api.saveCompositeView({ ...cv, name: dupViewName }, storageSettings)
          copiedExtras.push(`Composite View '${dupViewName}'`)
        } catch (e) {
          showToast('warning', `Composite View 복제 실패: ${e.message}`)
        }
      }

      const extra = copiedExtras.length ? ` (+ ${copiedExtras.join(', ')})` : ''
      showToast('success', `'${sourceName}' → '${finalName}' 복제 완료${extra}`)
      setDuplicateDialog(null)
      await loadQueries()
    } catch (e) {
      showToast('error', `복제 실패: ${e.message}`)
    } finally {
      setDuplicating(false)
    }
  }, [duplicateDialog, savedQueries, savedMetricViews, savedCompositeViews, storageSettings, showToast, loadQueries])

  // ── KBNL-74: 추천 모달 열기 ────────────────────────────────────────
  // 변환 결과 유형(yamlType)에 따라 분기:
  //   - composite : sub-SV picker 드롭다운 → 사용자가 SV 선택 → 모달
  //   - single    : 변환된 SV 의 name+description+tables 로 query → 모달 직접 오픈
  //   - 기타      : sqlText fallback (변환 전 등) → 모달 직접 오픈
  const handleOpenRecommend = useCallback(() => {
    const hasYaml = semanticYaml.trim() && (yamlType === 'single' || yamlType === 'composite')
    if (!hasYaml && !sqlText.trim()) {
      showToast('warning', '먼저 SQL 을 입력하거나 변환하세요.')
      return
    }

    // composite → picker 표시 (모달은 picker 안 sub-SV 클릭 시 오픈)
    if (yamlType === 'composite') {
      setRecommendPickerOpen(true)
      return
    }

    // single SV → 변환 결과 YAML 을 seed 로 검색 (query 텍스트보다 정확 —
    // vector 질의 풍부 + identifier 채널 활성 + 동명 저장본 exclude)
    if (yamlType === 'single' && semanticYaml.trim()) {
      const sums = extractViewSummariesFromYaml(semanticYaml)
      const s = sums[0]
      if (s) {
        setRecommendSeed({ name: s.name, description: s.description, yaml: s.yaml })
        setRecommendOpen(true)
        return
      }
    }

    // fallback: SQL 기반 — recommendSeed 비워두면 dialog 가 mode='sql' 로 동작
    setRecommendSeed(null)
    setRecommendOpen(true)
  }, [semanticYaml, yamlType, sqlText, showToast])

  // picker 외부 클릭 시 닫기
  useEffect(() => {
    if (!recommendPickerOpen) return
    const onDocClick = (e) => {
      if (recommendPickerRef.current && !recommendPickerRef.current.contains(e.target)) {
        setRecommendPickerOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [recommendPickerOpen])

  // ── 변환 실행 ───────────────────────────────────────────────────────
  const handleConvert = useCallback(async () => {
    if (!sqlText.trim()) { showToast('warning', 'SQL을 입력하세요.'); return }
    if (!settings?.project_id) { showToast('error', '설정에서 Project ID를 입력하세요.'); return }

    setConverting(true)
    setElapsed(0)
    timerRef.current = setInterval(() => setElapsed(e => e + 1), 1000)

    try {
      const res = await api.sqlToSemantic({
        sql_text: sqlText,
        project_id: settings.project_id,
        location: settings.vertex_location || settings.location || APP_DEFAULTS.location,
        model_name: settings.model_name || APP_DEFAULTS.model_name,
        glossary_id: settings.glossary_id || '',
        glossary_ids: Array.isArray(settings.glossary_ids) ? settings.glossary_ids : [],
        reference_views: [],
        user_requirements: requirements,
        storage_type: storageSettings?.storage_type || APP_DEFAULTS.storage_type,
        gcs_path: storageSettings?.gcs_path || '',
        fs_project: storageSettings?.fs_project || '',
        fs_database: storageSettings?.fs_database || '',
        fs_collection: storageSettings?.fs_collection || '',
        user_id: storageSettings?.user_id || '',
      })

      const resultYaml = res.semantic_yaml || ''
      setSemanticYaml(resultYaml)
      // KBNL-72: 백엔드가 변환 응답에 token_count 추가 (api_server.py)
      setYamlTokenCount(res.token_count ?? null)
      const fixMsg = res.fix_attempts > 0 ? ` (자동 수정 ${res.fix_attempts}회)` : ''
      const detectedType = detectYamlType(resultYaml)
      const typeMsg = detectedType ? ` → ${typeLabel(detectedType)}` : ''
      showToast('success', `변환 완료${fixMsg}${typeMsg}`)
    } catch (e) {
      showToast('error', `변환 실패: ${e.message}`)
    } finally {
      setConverting(false)
      clearInterval(timerRef.current)
    }
  }, [sqlText, settings, storageSettings, savedCompositeViews, savedMetricViews, requirements, showToast])

  useEffect(() => {
    return () => { if (timerRef.current) clearInterval(timerRef.current) }
  }, [])

  // ── 스플리터 드래그 ─────────────────────────────────────────────────
  const onSplitterMouseDown = useCallback((e) => {
    e.preventDefault()
    const container = containerRef.current
    if (!container) return
    const sidebarWidth = 220
    const startX = e.clientX
    const startPct = editorPct

    const onMove = (ev) => {
      const containerRect = container.getBoundingClientRect()
      const availableWidth = containerRect.width - sidebarWidth
      if (availableWidth <= 0) return
      const dx = ev.clientX - startX
      const dpct = (dx / availableWidth) * 100
      const newPct = Math.min(75, Math.max(20, startPct + dpct))
      setEditorPct(newPct)
    }
    const onUp = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }, [editorPct])

  // ── CodeMirror 확장 ─────────────────────────────────────────────────
  const sqlExtensions = [sql(), search({ top: true }), Prec.highest(keymap.of([
    ...searchKeymap,
    { key: 'Mod-Shift-l', run: selectSelectionMatches, preventDefault: true },
  ]))]

  const yamlExtensions = [yaml(), search({ top: true }), Prec.highest(keymap.of([
    ...searchKeymap,
    { key: 'Mod-Shift-l', run: selectSelectionMatches, preventDefault: true },
  ]))]

  const formatElapsed = (sec) => {
    const m = Math.floor(sec / 60)
    const s = sec % 60
    return m > 0 ? `${m}분 ${s}초` : `${s}초`
  }

  // Saved SQL 검색 필터 — name + description 으로 매치
  const filteredSavedQueries = useMemo(() => {
    return filterByText(savedQueries, sqlQuery, ['name', 'description'])
  }, [savedQueries, sqlQuery])
  // KBNL-141(리뷰): 선택 항목 식별을 owner::name 복합 키로 — 동명이인 행 동시 선택 방지.
  const selectedQueryKey = selectedQuery ? `${selectedQueryOwner}::${selectedQuery}` : ''

  return (
    <div ref={containerRef} style={{ display: 'flex', gap: 0, width: '100%', height: '100%', overflow: 'hidden' }}>
      {/* ── 사이드바 ────────────────────────────────────────────────── */}
      <div className="composite-sidebar" style={{ flexShrink: 0 }}>
        {/* 저장된 SQL 목록 */}
        <div className="composite-sidebar-header">
          <span style={{ fontWeight: 600, fontSize: 13 }}>Saved SQL Queries</span>
          <button className="btn-xs" onClick={handleNew} title="새로 만들기"
            style={{ fontSize: 14, lineHeight: 1, padding: '0 4px' }}>+</button>
        </div>
        {/* Saved SQL 검색 bar (Ctrl+K) */}
        <div style={{ display: 'flex', padding: '4px 8px', borderBottom: '1px solid var(--border)', background: 'var(--bg-sidebar)' }}>
          <SearchInput
            value={sqlQuery}
            onChange={setSqlQuery}
            placeholder="SQL 검색 (Ctrl+K)"
            total={savedQueries.length}
            matched={filteredSavedQueries.length}
            compact
          />
        </div>
        {/* 사이드바 전체 높이 차지 — Reference Views 섹션 제거 후 maxHeight / borderBottom 불필요.
            KBNL-49: 협업 모드 — 모든 user:* scope 통합. 본인 항목엔 "나" 배지, 다른 사용자
            항목엔 작성자 이름 노출 + rename/save/delete 차단(다른 곳에서 보호) + onclick 시
            owner_user_id 를 같이 전달해서 정확한 cross-owner 조회. */}
        <div className="std-list">
          {savedQueries.length === 0 ? (
            <div className="std-list-empty">저장된 SQL이 없습니다.</div>
          ) : filteredSavedQueries.length === 0 ? (
            <div className="std-list-empty">조건에 맞는 SQL 없음</div>
          ) : (
            filteredSavedQueries.map(q => {
              const isMine = q.is_mine !== false
              const ownerName = q.owner_name || q.owner_user_id || ''
              const tooltip = isMine
                ? '내가 만든 SQL (더블클릭으로 이름 변경)'
                : `작성자: ${ownerName}${q.owner_user_id ? ` (${q.owner_user_id})` : ''} — 읽기 전용 (저장 시 내 워크스페이스에 복사)`
              // KBNL-141(리뷰): owner::name 복합 키로 선택/리네임 판정 (동명이인 충돌 방지).
              const qKey = `${q.owner_user_id || ''}::${q.name}`
              const isRenamingThis = renamingQuery === qKey
              return (
                <div
                  key={qKey}
                  onClick={() => { if (!isRenamingThis) handleSelectQuery(q.name, q.owner_user_id || '') }}
                  onDoubleClick={() => { if (isMine) handleStartRename(q) }}
                  className={`std-list-item ${selectedQueryKey === qKey ? 'selected' : ''}`}
                  title={tooltip}
                >
                  <div className="std-list-name-row">
                    {isRenamingThis ? (
                      <input
                        autoFocus
                        className="std-list-rename-input"
                        value={renameValue}
                        onChange={e => setRenameValue(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') handleConfirmRename()
                          if (e.key === 'Escape') handleCancelRename()
                        }}
                        onBlur={handleConfirmRename}
                        onClick={e => e.stopPropagation()}
                        onDoubleClick={e => e.stopPropagation()}
                      />
                    ) : (
                      <span className="std-list-name">
                        <HighlightText text={q.name} query={sqlQuery} />
                      </span>
                    )}
                    {!isRenamingThis && (
                      <span className="std-list-owner" title={tooltip}>
                        {isMine
                          ? <span className="std-list-user-badge">나</span>
                          : <HighlightText text={ownerName} query={sqlQuery} />
                        }
                      </span>
                    )}
                    {/* 본인 항목에만 노출되는 hover 액션 — 복제 (다이얼로그에서 새 이름 확정) */}
                    {isMine && !isRenamingThis && (
                      <button
                        className="std-list-action-btn"
                        title="복제 (연결된 Semantic / Composite View 도 함께 복제됨)"
                        onClick={(e) => { e.stopPropagation(); openDuplicate(q) }}
                      >⎘</button>
                    )}
                  </div>
                  {!isRenamingThis && q.description && (
                    <div className="std-list-desc">
                      <HighlightText text={q.description} query={sqlQuery} />
                    </div>
                  )}
                  {!isRenamingThis && (q.updated_at || q.token_count != null) && (
                    <div className="std-list-meta">
                      {q.updated_at && <span>{String(q.updated_at).slice(0, 10)}</span>}
                      <TokenBadge count={q.token_count} />
                    </div>
                  )}
                </div>
              )
            })
          )}
        </div>

      </div>

      {/* ── 2열: SQL 에디터 ─────────────────────────────────────────────── */}
      <div style={{ flex: editorPct, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
        {/* 저장+변환 바 — 1행: 이름 + 변환/저장/삭제/새로만들기, 2행: 설명, 3행: 변환 프롬프트
            KBNL-72: 좁은 폭에서도 안 끼이게 — input 은 flex:1 로 신축, 버튼 그룹은 flex-wrap 허용 */}
        <div className="composite-save-bar" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 6 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ fontSize: 13, color: 'var(--text-dim)', whiteSpace: 'nowrap', minWidth: 72 }}>
              SQL 이름
            </label>
            <input
              value={queryName}
              onChange={e => setQueryName(e.target.value)}
              placeholder="SQL 쿼리 이름"
              className="field field-name"
              style={{ flex: '1 1 160px', minWidth: 0, width: 'auto' }}
            />
            <div className="btn-actions" style={{ flexShrink: 0 }}>
              <button
                className="btn-primary"
                onClick={handleConvert}
                disabled={converting || !sqlText.trim()}
                title="SQL 을 Semantic View YAML 로 변환"
              >
                {converting ? '변환 중...' : '▶ Semantic View로 변환'}
              </button>
              {/* KBNL-74 후속: "유사 SV 찾기" 버튼은 SQL 측이 아닌 SV/CV 측 헤더로 이동 —
                  검색 기준이 변환 결과(SV/CV) 이므로 그쪽 헤더에 두는 것이 의미상 자연스러움. */}
              <button className="btn-secondary" onClick={handleSave} disabled={saving}>
                {saving ? '저장 중...' : '저장'}
              </button>
              {selectedQuery && (
                <>
                  <button className="btn-danger" onClick={handleDelete} title="삭제">삭제</button>
                  <button className="btn-secondary" onClick={handleNew} title="새로 만들기">+ 새로 만들기</button>
                </>
              )}
            </div>
          </div>
          {/* KBNL-74: 추천 모달 — 트리거 버튼 클릭 시 오픈.
              후보 행 클릭 시 해당 SV 화면(SV 탭)으로 이동. */}
          {/* 랭킹 품질: 변환 결과 SV(recommendSeed)가 있으면 seed_sv + override_yaml
              로 검색, 없으면 mode='sql' 로 sqlText 기반 (변환 전 fallback). */}
          <SvRecommendDialog
            open={recommendOpen}
            onClose={() => setRecommendOpen(false)}
            mode={recommendSeed ? 'seed_sv' : 'sql'}
            sqlText={sqlText}
            seedSvName={recommendSeed?.name || ''}
            seedSvOverrideYaml={recommendSeed?.yaml || ''}
            seedSvOverrideDescription={recommendSeed?.description || ''}
            storageSettings={storageSettings}
            showToast={showToast}
            editorTheme={editorTheme}
            onSelect={(c) => {
              if (c?.name && onNavigateToView) {
                onNavigateToView(c.name, c.owner_user_id || c.user_id || '')
              } else if (c?.name) {
                showToast?.('warning', '이 화면에서는 SV 화면으로 이동할 수 없습니다.')
              }
            }}
            title={
              recommendSeed
                ? `'${recommendSeed.name}' 와(과) 유사한 Semantic View`
                : '유사 SV 찾기 — SQL 기반'
            }
          />

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <label style={{ fontSize: 13, color: 'var(--text-dim)', whiteSpace: 'nowrap', minWidth: 72 }}>
              설명
            </label>
            <textarea
              value={queryDesc}
              onChange={e => setQueryDesc(e.target.value)}
              placeholder="이 SQL 쿼리에 대한 설명 (선택)"
              className="field field-desc"
            />
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <label style={{ fontSize: 13, color: 'var(--text-dim)', whiteSpace: 'nowrap', minWidth: 72 }}>
              변환 프롬프트
            </label>
            <textarea
              value={requirements}
              onChange={e => setRequirements(e.target.value)}
              placeholder="추가 요구사항 (Oracle→BQ 변환 등)"
              className="field field-prompt"
            />
          </div>
        </div>

        {/* SQL 에디터 헤더 */}
        <div style={{ padding: '4px 12px', fontSize: 12, fontWeight: 600, borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <span>SQL</span>
          <span style={{ fontSize: 10, color: 'var(--text-dim)', fontWeight: 400 }}>
            {sqlText.split('\n').length} lines
          </span>
          {/* KBNL-69: 본문 헤더 토큰 배지 — 마지막 저장된 sql_text 의 토큰 수 */}
          {selectedQuery && (
            <TokenBadge
              count={selectedQueryTokenCount}
              variant="strong"
              title="이 SQL 본문의 저장된 토큰 수 — 편집 후 저장 시 갱신"
            />
          )}
          <span style={{ flex: 1 }} />
          {/* SQL 다운로드 (KBNL-27) */}
          <button
            className="btn-xs"
            onClick={() => downloadText(ensureExtension(queryName || selectedQuery || 'query', 'sql'), sqlText, 'application/sql')}
            disabled={!sqlText.trim()}
            style={{ fontSize: 11 }}
            title="현재 SQL 을 로컬 파일로 다운로드"
          >⤓ 다운로드</button>
        </div>

        {/* SQL CodeMirror */}
        <div className="editor-wrap" style={{ flex: 1 }}>
          <CodeMirror
            value={sqlText}
            onChange={setSqlText}
            extensions={sqlExtensions}
            theme={editorTheme === 'light' ? undefined : editorTheme}
            style={{ height: '100%' }}
            basicSetup={{ lineNumbers: true, foldGutter: true, bracketMatching: true }}
          />
        </div>
      </div>

      {/* ── 스플리터 ─────────────────────────────────────────────────────── */}
      <div
        className="composite-splitter"
        onMouseDown={onSplitterMouseDown}
      />

      {/* ── 3열: YAML 에디터 ──────────────────────────────────────────────── */}
      <div style={{ flex: 100 - editorPct, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
        {/* YAML 헤더
            KBNL-72: 좁은 폭에서도 안 끼이게 — viewName input 은 flex 신축, flex-wrap 으로 줄바꿈 허용.
                     TokenBadge 도 좌측 lines/empty 옆에 표시 (변환/저장 응답의 token_count 사용).
                     폰트/입력 사이즈는 버튼 행과 동일한 정상 크기로 통일 (14px input · 13px label). */}
        <div className="composite-editor-header" style={{ flexWrap: 'wrap', rowGap: 6, gap: 8, padding: '8px 10px' }}>
          <span style={{ fontWeight: 600, fontSize: 14 }}>Semantic View YAML</span>
          <span style={{ fontSize: 12, color: 'var(--text-dim)', fontWeight: 400 }}>
            {semanticYaml ? `${semanticYaml.split('\n').length} lines` : 'empty'}
          </span>
          {yamlType && (
            <span className={`sql-type-badge ${typeBadgeClass(yamlType)}`} style={{ fontSize: 12, padding: '3px 10px' }}>
              {typeLabel(yamlType)}
            </span>
          )}
          {/* KBNL-72: YAML 본문 토큰 배지 — 변환/저장 직후 갱신, 직접 편집 시 null 로 리셋 */}
          {semanticYaml && (
            <TokenBadge
              count={yamlTokenCount}
              variant="strong"
              title="Semantic View YAML 의 토큰 수 — 변환·저장 시 갱신. 직접 편집하면 null 로 리셋."
            />
          )}
          <span style={{ flex: 1, minWidth: 0 }} />
          {semanticYaml && (
            <>
              {/* KBNL-72: 입력/버튼 폰트·패딩은 다른 영역과 동일한 정상 크기로 복원.
                  좁은 폭에서는 flex-wrap 으로 자연스럽게 2행으로 떨어진다.
                  composite-name-input 의 작은 기본값(font 12 · padding 4×8)을 input.field 와
                  맞춰 inline override (14px font · 6×8 padding). */}
              <input
                value={viewName}
                onChange={e => setViewName(e.target.value)}
                placeholder={queryName ? `View 이름 (비우면 '${queryName}')` : 'View 이름 (비우면 SQL 이름과 동일)'}
                className="composite-name-input"
                style={{ flex: '1 1 200px', minWidth: 0, fontSize: 14, padding: '6px 8px' }}
                title="Semantic/Composite View 저장 시 사용할 이름. 비워두면 SQL 쿼리 이름과 동일하게 저장됩니다."
              />
              <button
                className="btn-primary"
                onClick={handleSave}
                disabled={saving || !queryName.trim()}
                style={{ flexShrink: 0 }}
                title={!queryName.trim() ? '왼쪽에서 SQL 쿼리 이름을 먼저 입력하세요' : ''}
              >
                {saving ? '저장 중...' : yamlType === 'single' ? 'Semantic View 저장' : yamlType === 'composite' ? 'Composite View 저장' : 'View 저장'}
              </button>
              {/* KBNL-74 후속: 유사 SV 찾기 — 변환된 SV/CV 기준 검색.
                  - single: 변환된 SV 의 name/desc/tables 기반 모달 즉시 오픈
                  - composite: ▾ picker 로 sub-SV 선택 후 모달 오픈 */}
              <div
                ref={recommendPickerRef}
                style={{ position: 'relative', display: 'inline-block', flexShrink: 0 }}
              >
                <button
                  className="btn-secondary"
                  onClick={handleOpenRecommend}
                  title={
                    yamlType === 'composite'
                      ? '변환 결과의 sub-SV 별로 유사 SV 찾기'
                      : '변환된 Semantic View 와 유사한 기존 SV 찾기'
                  }
                >
                  🔍 유사 SV 찾기{yamlType === 'composite' ? ' ▾' : ''}
                </button>
                {recommendPickerOpen && yamlType === 'composite' && (
                  <ul className="sv-recommend-picker">
                    {extractViewSummariesFromYaml(semanticYaml).map(s => (
                      <li
                        key={s.name}
                        onClick={() => {
                          setRecommendSeed({ name: s.name, description: s.description, yaml: s.yaml })
                          setRecommendPickerOpen(false)
                          setRecommendOpen(true)
                        }}
                        title={`'${s.name}' 와 유사한 SV 찾기`}
                      >
                        <strong>{s.name}</strong>
                        {s.description && (
                          <span className="picker-desc">— {s.description.slice(0, 40)}</span>
                        )}
                      </li>
                    ))}
                    {extractViewSummariesFromYaml(semanticYaml).length === 0 && (
                      <li className="picker-empty">sub-SV 를 찾지 못했습니다.</li>
                    )}
                  </ul>
                )}
              </div>
              <button className="btn-secondary" style={{ flexShrink: 0 }}
                onClick={() => { copyToClipboard(semanticYaml).then(() => showToast('success', 'YAML 복사 완료')).catch(() => showToast('error', '클립보드 복사 실패')) }}>
                복사
              </button>
              {/* YAML 다운로드 (KBNL-27) */}
              <button
                className="btn-secondary"
                style={{ flexShrink: 0 }}
                onClick={() => downloadText(
                  ensureExtension((viewName || queryName || selectedQuery || 'semantic_view'), 'yaml'),
                  semanticYaml,
                  'text/yaml',
                )}
                title="현재 Semantic View YAML 을 로컬 파일로 다운로드"
              >⤓ 다운로드</button>
            </>
          )}
        </div>

        {/* YAML CodeMirror */}
        <div className="editor-wrap" style={{ flex: 1 }}>
          <CodeMirror
            value={semanticYaml}
            onChange={(v) => { setSemanticYaml(v); setYamlTokenCount(null) }}
            extensions={yamlExtensions}
            theme={editorTheme === 'light' ? undefined : editorTheme}
            style={{ height: '100%' }}
            basicSetup={{ lineNumbers: true, foldGutter: true, bracketMatching: true }}
            placeholder="변환 결과가 여기에 표시됩니다. 직접 편집도 가능합니다."
          />
        </div>
      </div>

      {/* ── 동명 레코드 덮어쓰기 확인 다이얼로그 ──────────────────────── */}
      {overwriteDialog && (
        <div
          className="explore-copy-overlay"
          style={{ position: 'fixed', zIndex: 1500 }}
          onClick={() => !saving && setOverwriteDialog(null)}
        >
          <div className="explore-copy-dialog" onClick={e => e.stopPropagation()}>
            <h3>이미 존재하는 항목 덮어쓰기</h3>
            <div className="form-hint" style={{ marginBottom: 8 }}>
              다음 항목이 이미 존재합니다. 저장하면 기존 내용을 덮어씁니다.
            </div>
            <ul style={{ margin: '6px 0 4px 18px', padding: 0, fontSize: 13 }}>
              {overwriteDialog.overwrites.map((o, i) => (
                <li key={i} style={{ margin: '2px 0' }}>{o}</li>
              ))}
            </ul>
            <div className="explore-copy-actions">
              <button className="btn-secondary" onClick={() => setOverwriteDialog(null)} disabled={saving}>
                취소
              </button>
              <button className="btn-primary" onClick={confirmOverwrite} disabled={saving}>
                덮어쓰기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── 복제 다이얼로그 ───────────────────────────────────────────── */}
      <DuplicateDialog
        open={!!duplicateDialog}
        defaultName={duplicateDialog?.defaultName || ''}
        busy={duplicating}
        title="SQL 쿼리 복제"
        hint="연결된 Semantic View / Composite View 도 같은 이름 규칙으로 함께 복제됩니다."
        onConfirm={handleDuplicate}
        onCancel={() => { if (!duplicating) setDuplicateDialog(null) }}
      />

      {/* ── 변환 로딩 오버레이 ──────────────────────────────────────────── */}
      {converting && (
        <div className="overlay">
          <div className="spinner-box">
            <div className="spinner" />
            <div className="spinner-text">{SPINNER_MESSAGES[spinnerIdx]}</div>
            <div className="spinner-elapsed">{formatElapsed(elapsed)}</div>
          </div>
        </div>
      )}
    </div>
  )
}
